import type {
  SupplierResponsiblePersonOptionsQueryParams,
  SupplierResponsiblePersonOptionsResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import {
  zodSupplierResponsiblePersonOptionsQueryParamsSchema,
  zodSupplierResponsiblePersonOptionsResponseSchema,
} from './validations';

const EMPTY_RESPONSE: SupplierResponsiblePersonOptionsResponse = { data: [] };

/**
 * Fetches the people selectable in the supplier overview's responsible-person
 * filter: everyone owning at least one supplier relationship of the given
 * recipient companies, sorted by surname.
 *
 * @param queryParams - The recipient company IDs to scope the options to.
 * @param options - Optional ky request options (e.g. abort signal).
 * @returns The responsible-person filter options.
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_supplier_responsible_person_options_suppliers_responsible_persons_get
 */
export const fetchSupplierResponsiblePersonOptions = async (
  queryParams: SupplierResponsiblePersonOptionsQueryParams,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.SUPPLIER.GET_RESPONSIBLE_PERSON_OPTIONS(), {
      searchParams: queryParams,
      validate: {
        searchParams: zodSupplierResponsiblePersonOptionsQueryParamsSchema,
      },
      ...options,
    })
    .json<SupplierResponsiblePersonOptionsResponse>();

  const validatedResponse =
    zodSupplierResponsiblePersonOptionsResponseSchema.safeParse(response);

  if (!validatedResponse.success) {
    log.error({
      message:
        'Supplier responsible person options API response has validation errors',
      error: {
        errors: validatedResponse.error.issues,
        errorMessage: validatedResponse.error.message,
        response,
      },
    });

    // Consumers dereference `data` directly, so a malformed response must
    // not reach them unvalidated.
    return EMPTY_RESPONSE;
  }

  return validatedResponse.data;
};
